import http from './http';
import {
  InterviewObservabilityContext,
  toInterviewObservabilityHeaders,
} from '../utils/interviewObservability';

const INTERVIEW_START_TIMEOUT_MS = 90000;
// Synchronous evaluation can legitimately exceed the generic 15s envelope;
// keep this finite and below the 120s submit-attempt lease.
export const INTERVIEW_ANSWER_SUBMIT_TIMEOUT_MS = 90000;
const INTERVIEW_TTS_TIMEOUT_MS = 10000;
const INTERVIEW_TRANSCRIBE_TIMEOUT_MS = 180000;
const INTERVIEW_VIDEO_UPLOAD_TIMEOUT_MS = 240000;

export const interviewService = {
  /** Siapkan pertanyaan lengkap lebih awal tanpa memulai sesi kandidat. */
  prepare: (
    outsource_id: number,
    job_id: number,
    observability?: InterviewObservabilityContext,
  ) => http.post('interview/prepare', { outsource_id, job_id }, {
    timeout: INTERVIEW_START_TIMEOUT_MS,
    headers: toInterviewObservabilityHeaders(observability),
  }),

  /** Mulai interview baru atau lanjutkan yang in_progress */
  start: (
    outsource_id: number,
    job_id: number,
    confidence_consent: boolean,
    observability?: InterviewObservabilityContext,
  ) => http.post('interview/start', { outsource_id, job_id, confidence_consent }, {
    timeout: INTERVIEW_START_TIMEOUT_MS,
    headers: toInterviewObservabilityHeaders(observability),
  }),

  /** Get interview by id */
  get: (interview_id: number, observability?: InterviewObservabilityContext) =>
    http.get('interview/get', {
      params: { interview_id },
      headers: toInterviewObservabilityHeaders(observability),
    }),

  /** Get interview by outsource_id + job_id */
  getByApplication: (
    outsource_id: number,
    job_id: number,
    observability?: InterviewObservabilityContext,
  ) => http.get('interview/get', {
    params: { outsource_id, job_id },
    headers: toInterviewObservabilityHeaders(observability),
  }),

  /** List semua interview milik kandidat (auth dari Bearer token) */
  list: () => http.get('interview'),

  /** Submit jawaban untuk satu pertanyaan */
  submitAnswer: (
    interview_id: number,
    data: {
      question_id: number;
      answer_text: string;
      raw_answer?: string;
      transcript_reviewed?: boolean;
      transcript_risk_acknowledged?: boolean;
      transcript_quality_score?: number | null;
      transcript_review_reason?: string | null;
      transcript_quality_flags?: string[];
      transcript_risky_shift_detected?: boolean;
      transcript_prefer_raw?: boolean;
      transcript_mode?: string | null;
      client_attempt_id: string;
      video_url?: string | null;
      video_mime_type?: string | null;
      video_size_bytes?: number | null;
      video_duration_seconds?: number | null;
      time_spent_seconds: number;
      auto_submit?: boolean;
      conversation_mode?: boolean;
      seamless_mode?: boolean;
    },
    observability?: InterviewObservabilityContext,
  ) => http.post(`interview/${interview_id}/answer`, data, {
    timeout: INTERVIEW_ANSWER_SUBMIT_TIMEOUT_MS,
    headers: toInterviewObservabilityHeaders(observability),
  }),

  /** Selesaikan interview & trigger final AI scoring */
  finish: (interview_id: number, total_time_seconds: number) =>
    http.post(`interview/${interview_id}/finish`, { total_time_seconds }),

  /** Get hasil interview */
  getResult: (interview_id: number) =>
    http.get(`interview/${interview_id}/result`, {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    }),

  /** Get progress pemrosesan hasil interview */
  getProcessing: (interview_id: number) =>
    http.get(`interview/${interview_id}/processing`, {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache' },
    }),

  /** Get detail lengkap Q&A + scores */
  getDetail: (interview_id: number, observability?: InterviewObservabilityContext) =>
    http.get(`interview/${interview_id}/detail`, {
      headers: toInterviewObservabilityHeaders(observability),
    }),

  technicalInterruption: (
    interview_id: number,
    data: {
      reason_code: string;
      checkpoint: string;
    },
    observability?: InterviewObservabilityContext,
  ) => http.post(`interview/${interview_id}/technical-interruption`, data, {
    headers: toInterviewObservabilityHeaders(observability),
  }),

  synthesizeSpeech: (
    interview_id: number,
    text: string,
    context: 'personalized_intro' | 'greeting' | 'question' = 'question',
    observability?: InterviewObservabilityContext,
    requestTimeoutMs?: number,
  ) =>
    http.post('interview/tts', {
      interview_id,
      text,
      context,
    }, {
      responseType: 'blob',
      timeout: Number.isFinite(Number(requestTimeoutMs)) && Number(requestTimeoutMs) > 0
        ? Math.min(INTERVIEW_TTS_TIMEOUT_MS, Math.max(1, Math.round(Number(requestTimeoutMs))))
        : INTERVIEW_TTS_TIMEOUT_MS,
      headers: {
        Accept: 'audio/mpeg',
        ...toInterviewObservabilityHeaders(observability),
      },
    }),

  /** Transkripsi audio menggunakan Whisper via backend */
  transcribeAudio: (
    audioBlob: Blob,
    jobId?: number,
    questionText?: string,
    interviewId?: number,
    options?: {
      conversationMode?: boolean;
      transcriptPipeline?: 'conversation' | 'standard' | 'review';
      timeoutMs?: number;
      observability?: InterviewObservabilityContext;
    }
  ) => {
    // Strip codec params (e.g. "audio/webm;codecs=opus") — AdonisJS bodyparser rejects them
    const safeMime = audioBlob.type.split(';')[0] || 'audio/webm';
    const safeBlob = new Blob([audioBlob], { type: safeMime });
    const audioFilename = safeMime.includes('mp4')
      ? 'audio.mp4'
      : safeMime.includes('ogg')
      ? 'audio.ogg'
      : safeMime.includes('wav')
      ? 'audio.wav'
      : 'audio.webm';
    const form = new FormData();
    form.append('audio', safeBlob, audioFilename);
    if (jobId) {
      form.append('job_id', String(jobId));
    }
    if (interviewId) {
      form.append('interview_id', String(interviewId));
    }
    if (questionText && questionText.trim()) {
      form.append('question_text', questionText.trim());
    }
    if (options?.conversationMode) {
      form.append('conversation_mode', 'true');
      form.append('transcript_pipeline', options.transcriptPipeline || 'conversation');
    }
    const requestedTimeoutMs = Number(options?.timeoutMs);
    const timeoutMs = Number.isFinite(requestedTimeoutMs) && requestedTimeoutMs > 0
      ? Math.min(INTERVIEW_TRANSCRIBE_TIMEOUT_MS, Math.max(1, Math.round(requestedTimeoutMs)))
      : INTERVIEW_TRANSCRIBE_TIMEOUT_MS;
    return http.post('interview/transcribe', form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...toInterviewObservabilityHeaders(options?.observability),
      },
      timeout: timeoutMs,
    });
  },

  uploadAnswerVideo: (
    videoBlob: Blob,
    options?: {
      filename?: string;
      path?: string;
      interviewId?: number;
      questionId?: number;
      clientAttemptId?: string;
      recordedAt?: string;
      videoDurationSeconds?: number;
      observability?: InterviewObservabilityContext;
    }
  ) => {
    const safeMime = videoBlob.type.split(';')[0] || 'video/webm';
    const safeBlob = new Blob([videoBlob], { type: safeMime });
    const form = new FormData();
    const resolvedFilename = options?.filename || 'interview-answer.webm';
    form.append('file', safeBlob, resolvedFilename);
    if (options?.path) {
      form.append('path', options.path);
      form.append('target_path', options.path);
    }
    if (options?.interviewId) {
      form.append('interview_id', String(options.interviewId));
    }
    if (options?.questionId) {
      form.append('question_id', String(options.questionId));
    }
    if (options?.clientAttemptId) {
      form.append('client_attempt_id', options.clientAttemptId);
    }
    if (options?.recordedAt) {
      form.append('recorded_at', options.recordedAt);
    }
    if (Number.isFinite(Number(options?.videoDurationSeconds))) {
      form.append('video_duration_seconds', String(Number(options?.videoDurationSeconds)));
    }
    form.append('filename', resolvedFilename);
    form.append('target_filename', resolvedFilename);

    return http.post('upload/cdn', form, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...toInterviewObservabilityHeaders(options?.observability),
      },
      timeout: INTERVIEW_VIDEO_UPLOAD_TIMEOUT_MS,
    });
  },
};
